import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { BrowserKind, CloudProvider, DeploymentType, HetznerLocation } from "./AgentInstallation.ts";

/**
 * Intake lifecycle status.
 * @since 1.0.0
 */
export const IntakeStatus = Schema.Literal(
  "draft",
  "sent",
  "signed_up",
  "subscribed",
  "provisioning",
  "completed",
  "abandoned",
);
export type IntakeStatus = Schema.Schema.Type<typeof IntakeStatus>;

/**
 * How the customer was acquired.
 * @since 1.0.0
 */
export const IntakeSource = Schema.Literal(
  "word_of_mouth",
  "ads",
  "social",
  "referral",
  "reseller",
  "other",
);
export type IntakeSource = Schema.Schema.Type<typeof IntakeSource>;

/**
 * Which install path a self-serve onboarding run takes: provision a brand-new
 * box (`install`) or adopt the customer's existing machine (`adopt`).
 *
 * @since 1.0.0
 */
export const IntakeInstallMode = Schema.Literal("install", "adopt");
export type IntakeInstallMode = Schema.Schema.Type<typeof IntakeInstallMode>;

/**
 * Intake model — the one-time, CSR-mediated acquisition record.
 *
 * 1:1 with a customer. Seeds the first agent and acts as the wizard catalog
 * filter (`selectedChannels` / `selectedConnectors` hold keys from the web
 * `wizard-catalog.ts`). The customer magic link is stored hashed at rest via
 * `linkTokenHash`.
 *
 * @since 1.0.0
 */
export class Intake extends Model.Class<Intake>("Intake")({
  id: Model.Generated(Schema.String),
  customerId: Schema.String,
  primaryPersonId: Schema.NullOr(Schema.String),
  createdByPersonId: Schema.NullOr(Schema.String),
  leadId: Schema.NullOr(Schema.String),
  source: IntakeSource,
  sourceDetail: Schema.NullOr(Schema.String),
  selectedChannels: Schema.Array(Schema.String),
  selectedConnectors: Schema.Array(Schema.String),
  selectedLlm: Schema.NullOr(Schema.String),
  deploymentType: Schema.NullOr(DeploymentType),
  cloudProvider: Schema.optionalWith(CloudProvider, { default: () => "digitalocean" as const }),
  browser: Schema.optionalWith(BrowserKind, { default: () => "chrome" as const }),
  // The CSR-captured cloud-vps region (Hetzner location slug); null when unset.
  // Only meaningful for a `cloud-vps` deployment; validated in the api layer.
  region: Schema.NullOr(HetznerLocation),
  // The negotiated one-time install fee in cents (null when un-priced). Carried
  // on the intake by the CSR worksheet, then billed by the onboarding embedded
  // checkout and copied onto the intake-seeded agent installation.
  installFeeAmount: Schema.NullOr(Schema.Int),
  // Self-serve seam markers, both null on a CSR-mediated intake. `selfServeAt`
  // stamps when the customer entered the self-serve wizard; `installMode` records
  // which path they chose. `Schema.DateTimeUtc` (not the Date-based Model variant)
  // for the same wire-form reason as the link fields below.
  selfServeAt: Schema.NullOr(Schema.DateTimeUtc),
  installMode: Schema.NullOr(IntakeInstallMode),
  status: Schema.optionalWith(IntakeStatus, { default: () => "draft" as const }),
  linkTokenHash: Schema.NullOr(Schema.String),
  // `Schema.DateTimeUtc` (not `Model.DateTimeFromDate`) so the wire/JSON form is
  // an ISO string, not a `Date` instance. The Date-based variant encodes to a
  // `Date` whose JSON serialization is a string the decoder then rejects —
  // breaking every RPC response that carries a non-null link timestamp
  // (intake list + finalize). The runtime type is `DateTime.Utc | null` either way.
  linkExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  linkResolvedAt: Schema.NullOr(Schema.DateTimeUtc),
  // Short-lived onboarding session credential (CLA-841): the SHA-256 hash of the
  // session id minted by the link→cookie exchange, and its 24h expiry. Both null
  // until the wizard performs the exchange. `Schema.DateTimeUtc` (not the
  // Date-based Model variant) for the same wire-form reason as the link fields.
  sessionTokenHash: Schema.NullOr(Schema.String),
  sessionExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  notes: Schema.NullOr(Schema.String),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
